import { Admin } from '@models/Admin'
import { useAuth } from '@modules/auth/contexts/Auth'
import { Tenant } from '@models/Tenant'
import { DatadogProvider, withTimeout } from '@datadog/openfeature-browser'
import {
  EvaluationContext,
  OpenFeature,
  ProviderEvents,
  ProviderStatus,
  StandardResolutionReasons,
  useBooleanFlagDetails,
  useBooleanFlagValue,
  useOpenFeatureClient,
} from '@openfeature/react-sdk'
import { useEffect, useReducer, useSyncExternalStore } from 'react'
import { DATADOG } from './datadog'

const env = import.meta.env.VITE_ENVIRONMENT

export const isDev = env === 'dev'
export const isProduction = env === 'production'

/**
 * Developer tools, driven by VITE_ENVIRONMENT only: they are never meant for
 * production, so they have no Datadog flag.
 */
export const localFlags = {
  // Axios interceptor that mocks analytics-graph responses (toggled via
  // localStorage). Enabled in dev + staging so charts can be faked when
  // demoing staging to a prospect; opt-in per browser, defaults off.
  analyticsInterceptor: !isProduction,
  // Floating DevTools button hosting the mock-interceptor toggle. Enabled in
  // dev + staging so the toggle is reachable during staging demos.
  devTools: !isProduction,
  // Console warning when a <Localizable> wrapper is missing a LangSelector
  localizableWarning: isDev,
} as const

/**
 * Features that can ship switched off in production. A feature runs when its
 * Datadog flag (`key`) says so, which lets the product owner turn it on, for
 * everyone or per email domain, without a deploy. Staging reads its own
 * Datadog environment, where a flag is on unless it is turned off there, so
 * every feature keeps being tested before it goes live; local never asks
 * Datadog and runs every feature.
 *
 * Until Datadog answers, and whenever it cannot (unreachable, or the flag does
 * not exist), a flag holds its fallback: `true` outside production,
 * `productionFallback` in production. A feature already released to everyone
 * keeps `productionFallback: true`, so an outage never takes it away.
 *
 * Releasing a feature to everyone in production is done by switching the
 * production default variant to `true`, never by a targeting rule serving
 * everyone: a value served by a targeting rule is what marks a feature as a
 * preview for internal admins (see `useFeaturePreview`), so such a rule would
 * keep the `Preview` tag on a feature every client already has.
 *
 * Cutting a feature (in staging, or in production once its
 * `productionFallback` is `true`) is done by serving its `false` variant from
 * Datadog, not by disabling the flag or the environment there: a disabled
 * flag/environment makes Datadog not answer for it, which falls back to the
 * fallback above (`true` in staging, `productionFallback` in production) —
 * the opposite of a cut. Same convention as Alela.
 *
 * Keys: `plethore.<domain>.<feature>`, each segment in kebab-case. The domain
 * is the back office's own vocabulary (venues, users, marketing, membership,
 * settings); the feature is a noun, without `enable`, `new`, a version, an
 * environment or a ticket. A Datadog key cannot be renamed and is public,
 * since it is sent to the browser.
 *
 * A flag guards every entry point of its feature. Once a feature is on for
 * good, delete its flag, the checks that read it and the Datadog flag.
 */
const FeatureFlags = {
  /** Venue External Booking API card */
  externalBookingApi: {
    key: 'plethore.venues.external-booking-api',
    productionFallback: false,
  },
  /** "Send test" buttons for communications (push/email test sends) */
  testCommunications: {
    key: 'plethore.marketing.test-communications',
    productionFallback: false,
  },
  /** Settings > User > Data privacy entry */
  userDataPrivacy: {
    key: 'plethore.settings.user-data-privacy',
    productionFallback: false,
  },
  /**
   * Membership Revenue page (/membership/revenue) and its two drill-downs
   * (transactions, renewals): the sub-nav tab and the routes. Off in
   * production until the membership revenue endpoints have run against real
   * production data. Also requires the `view revenue membership` permission.
   */
  membershipAnalyticsOverview: {
    key: 'plethore.membership.analytics-overview',
    productionFallback: false,
  },
  /**
   * "Change plan" action on an active or to-cancel membership subscription
   * (member profile > Subscription). Off in production until the bo-api
   * change-plan endpoint has run against real data.
   */
  changeSubscriptionPlan: {
    key: 'plethore.membership.change-subscription-plan',
    productionFallback: false,
  },
  /**
   * Creating a custom client segment. The Users > Segments page is always
   * reachable; only the create action is gated, until the bo-api /audiences
   * criteria + preview-count endpoints ship (#276).
   */
  customSegments: {
    key: 'plethore.users.custom-segments',
    productionFallback: false,
  },
  /**
   * Website visibility/booking cards (General + Booking tabs) and the
   * seating-area web booking switch. The webapp itself has no production
   * deployment yet, so the surface is already dark there -- this only gates
   * the back-office UI while the flags are rolled out.
   */
  webBooking: {
    key: 'plethore.venues.web-booking',
    productionFallback: false,
  },
} satisfies Record<string, { key: string; productionFallback: boolean }>

export type FeatureFlag = keyof typeof FeatureFlags

// Same test as Datadog RUM in main.tsx: local never asks Datadog
const isDatadogEnabled = !isDev

// Datadog only accepts flat primitives in the evaluation context. Not
// actually anonymous while a RUM user is still set -- see the
// enableRumFeatureFlagTracking comment in initFeatureFlags.
const ANONYMOUS_CONTEXT: EvaluationContext = {}

const evaluationContextOf = (
  admin: Admin | undefined,
  tenant: Tenant
): EvaluationContext => {
  if (admin === undefined) return ANONYMOUS_CONTEXT
  const email = admin.email.toLowerCase()
  const roles = roleNamesOf(admin)
  return {
    targetingKey: String(admin.id),
    email,
    email_domain: email.split('@').pop() ?? '',
    organization_id: tenant.envName,
    // One boolean per role (Datadog accepts no arrays): a rule targets a role
    // with `role_membership` is true.
    ...Object.fromEntries(roles.map((role) => [`role_${role}`, true])),
  }
}

/**
 * Role names of an admin. `Admin.roles` is typed `number[]`, but `/me` sends
 * the Role resources (`{ id, name }`), which is what `HasAccess` reads too.
 */
const roleNamesOf = (admin: Admin): string[] =>
  ((admin.roles ?? []) as unknown as Array<{ name?: unknown } | number>)
    .map((role) => (typeof role === 'object' ? role?.name : undefined))
    .filter((name): name is string => typeof name === 'string')
    .sort()

/**
 * Registers Datadog as the flag source, once, before the first render. The app
 * never waits for it: flags hold their production fallback until it answers,
 * then the components reading them re-render.
 */
export const initFeatureFlags = () => {
  if (!isDatadogEnabled) return

  try {
    OpenFeature.setProvider(
      new DatadogProvider({
        applicationId: DATADOG.applicationId,
        clientToken: DATADOG.clientToken,
        site: DATADOG.site,
        service: DATADOG.service,
        env,
        version: __APP_VERSION__,
        // A hung request must not hold the fallback values for long
        flagConfigurationFetch: withTimeout(globalThis.fetch, 5_000),
        // Each RUM session records the flags its admin had. This single
        // option also turns on the provider's RUM context enrichment
        // (enrichEvaluationContextWithRumUser, in
        // @datadog/openfeature-browser/esm/openfeature/rumIntegration.js):
        // every evaluation context, including every setContext call below,
        // gets DD_RUM.getUser()'s id/name/email merged in first, our own
        // fields then overriding theirs. The SDK exposes no separate switch
        // to keep flag tracking without this merge, so the "anonymous"
        // context on logout is only as anonymous as RUM is -- which is why
        // on logout, datadogRum.clearUser() (App.tsx `loggedOut`) must run, and
        // does run, before useFeatureFlagsContext reacts to currentUser going
        // undefined and calls setContext(ANONYMOUS_CONTEXT) below.
        enableRumFeatureFlagTracking: true,
        // No experiment reads exposures: targeting is resolved by Datadog
        // from the context alone
        enableExposureLogging: false,
        enableFlagEvaluationTracking: false,
      }),
      ANONYMOUS_CONTEXT
    )
  } catch {
    // A broken configuration leaves every flag on its production fallback
  }
}

/**
 * Keeps the evaluation context on the logged admin and the current tenant,
 * back to anonymous on logout. Mounted once, in `SessionIdentity`.
 */
export const useFeatureFlagsContext = (
  admin: Admin | undefined,
  tenant: Tenant
) => {
  const adminId = admin?.id
  const email = admin?.email
  const roleKey = admin ? roleNamesOf(admin).join(',') : ''
  const tenantName = tenant.envName

  useEffect(() => {
    if (!isDatadogEnabled) return

    const context = evaluationContextOf(admin, tenant)
    const sync = async () => {
      try {
        await OpenFeature.setContext(context)
      } catch {
        // Flags keep their production fallback until the next change
      }
    }
    sync()
    // Only the fields the context is built from
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId, email, roleKey, tenantName])
}

const fallbackOf = (flag: FeatureFlag): boolean =>
  isProduction ? FeatureFlags[flag].productionFallback : true

/** Whether a feature runs for the current admin; re-renders when Datadog answers */
export const useFeatureFlag = (flag: FeatureFlag): boolean =>
  useBooleanFlagValue(FeatureFlags[flag].key, fallbackOf(flag))

/**
 * Internal (Supplément Bacon) admins: mirrors the Datadog saved filter
 * "Supplément Bacon email" (`email_domain` MATCHES `supplement-bacon.com`).
 * Gates the DevTools; no longer restricts who sees a preview mark.
 */
export const INTERNAL_EMAIL_DOMAIN = 'supplement-bacon.com'

export const isInternalAdmin = (admin: Admin | undefined): boolean =>
  admin?.email.toLowerCase().endsWith(`@${INTERNAL_EMAIL_DOMAIN}`) ?? false

/**
 * Whether the current admin has a feature only as a preview: Datadog served
 * its `true` through a targeting rule (`TARGETING_MATCH`: an email, a domain,
 * a role, a tenant…) rather than through the environment default (`STATIC`),
 * i.e. the feature is not enabled for everyone yet. Any targeted admin sees
 * the mark, not only internal ones. A fallback value (Datadog not answered
 * yet, unreachable, or local) carries no targeting reason, so it is never a
 * preview.
 */
export const useFeaturePreview = (
  flag: FeatureFlag,
  admin: Admin | undefined
): boolean => {
  const { value, reason } = useBooleanFlagDetails(
    FeatureFlags[flag].key,
    fallbackOf(flag)
  )
  return (
    admin !== undefined &&
    value &&
    reason === StandardResolutionReasons.TARGETING_MATCH
  )
}

/**
 * Per-browser switch hiding every `Preview` tag, for a demo or a screenshot.
 * It hides the tags only, never the features. Visible by default, and
 * whenever storage is unavailable.
 */
const PREVIEW_BADGES_HIDDEN_KEY = 'plethore.preview-badges-hidden'
const previewBadgesListeners = new Set<() => void>()

const readPreviewBadgesHidden = (): boolean => {
  try {
    return localStorage.getItem(PREVIEW_BADGES_HIDDEN_KEY) === '1'
  } catch {
    return false
  }
}

let previewBadgesHidden = readPreviewBadgesHidden()

export const setPreviewBadgesVisible = (visible: boolean) => {
  previewBadgesHidden = !visible
  try {
    if (visible) localStorage.removeItem(PREVIEW_BADGES_HIDDEN_KEY)
    else localStorage.setItem(PREVIEW_BADGES_HIDDEN_KEY, '1')
  } catch {
    // Kept for this page only
  }
  previewBadgesListeners.forEach((listener) => listener())
}

const subscribePreviewBadges = (listener: () => void) => {
  previewBadgesListeners.add(listener)
  return () => {
    previewBadgesListeners.delete(listener)
  }
}

export const usePreviewBadgesVisible = (): boolean =>
  useSyncExternalStore(subscribePreviewBadges, () => !previewBadgesHidden)

const SETTLING_EVENTS = [
  ProviderEvents.Ready,
  ProviderEvents.Reconciling,
  ProviderEvents.ContextChanged,
  ProviderEvents.ConfigurationChanged,
  ProviderEvents.Error,
  ProviderEvents.Stale,
]

/**
 * Whether the flags are final for this admin: Datadog has answered for their
 * own context, or failed to. Until then a flag holds its fallback, which a
 * route must not act on, or a deep link would be redirected away before
 * Datadog turns its feature on.
 */
export const useFeatureFlagsSettled = (admin: Admin | undefined): boolean => {
  const client = useOpenFeatureClient()
  const [, rerender] = useReducer((count: number) => count + 1, 0)

  useEffect(() => {
    const controller = new AbortController()
    SETTLING_EVENTS.forEach((event) =>
      client.addHandler(event, rerender, { signal: controller.signal })
    )
    return () => controller.abort()
  }, [client])

  if (!isDatadogEnabled) return true
  if (admin === undefined) return false

  const status = client.providerStatus
  return (
    status !== ProviderStatus.NOT_READY &&
    status !== ProviderStatus.RECONCILING &&
    OpenFeature.getContext().targetingKey === String(admin.id)
  )
}

/**
 * One answer to "does this admin get the feature, and as a preview?":
 * `enabled` is the flag itself (`useFeatureFlag`; permissions are not involved,
 * they stay with `HasAccess` / `useHasAccess`); `preview` is true when the
 * feature is on only as a preview for this internal admin AND the DevTools
 * switch has not hidden the previews. Menus, routes and `<Feature />` all
 * read this, so the check and the "Preview" marking cannot drift apart.
 */
export const useFeature = (
  flag: FeatureFlag
): { enabled: boolean; preview: boolean } => {
  const { currentUser } = useAuth<Admin, {}>()
  const enabled = useFeatureFlag(flag)
  const isPreview = useFeaturePreview(flag, currentUser)
  const badgesVisible = usePreviewBadgesVisible()

  return { enabled, preview: enabled && isPreview && badgesVisible }
}
